import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../../config";
import "./ACAConsentPage.css";

function ACAConsentPage() {
  const { token } = useParams();

  const [record, setRecord] = useState(null);
  const [agent, setAgent] = useState(null);
  const [typedSignature, setTypedSignature] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/aca-consent/session/${token}`);

        if (!res.ok) {
          setLoading(false);
          return;
        }

        const data = await res.json();
        setRecord(data.record || null);
        setAgent(data.agent || null);
        setLoading(false);
      } catch (err) {
        console.error("ACA Consent session load error:", err);
        setLoading(false);
      }
    };

    loadSession();
  }, [token]);

  const handleSubmit = async () => {
    if (!typedSignature.trim()) {
      alert("Please type your full name as your signature.");
      return;
    }

    if (!agreement) {
      alert("You must agree before submitting.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/aca-consent/sign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token,
          typed_signature: typedSignature,
          agreement_confirmed: agreement
        })
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Unable to submit ACA Consent.");
        return;
      }

      setSubmitted(true);
    } catch (err) {
      console.error("ACA Consent submit error:", err);
      alert("Error submitting ACA Consent.");
    }
  };

  const fullRecordName = `${record?.first_name || ""} ${record?.last_name || ""}`.trim();
  const agentName = agent?.name || "";
  const agentPhone = agent?.phone || "";
  const agentNpn = agent?.npn || "";

  if (loading) {
    return <div className="aca-consent-loading">Loading ACA Consent...</div>;
  }

  if (!record || !agent) {
    return (
      <div className="aca-consent-loading">
        This ACA Consent link is invalid or expired.
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="aca-consent-page">
        <div className="aca-consent-card">
          <h1 className="aca-consent-title">ACA Consent Submitted</h1>
          <p className="aca-consent-text">
            Thank you. Your ACA Consent form has been successfully signed and submitted.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="aca-consent-page">
      <div className="aca-consent-card">
        <h1 className="aca-consent-title">ACA Consent Form</h1>

        <p className="aca-consent-subtitle">
          Please review the consent language below and sign to continue.
        </p>

        <div className="aca-consent-scrollbox">
          <p>
            I, {fullRecordName}, give my permission to {agentName} ("Agent") to serve
            as the health insurance Agent or broker for myself and my entire household
            if applicable, for purposes of enrollment in a Qualified Health Plan offered
            on the Federally Facilitated Marketplace or State Based Exchange (SBE). By
            consenting to this agreement, I authorize the above-mentioned Agent to view
            and use the confidential information provided by me in writing,
            electronically, or by phone only for one or more of the following:
          </p>

          <ul>
            <li>Searching for an existing Marketplace or SBE application</li>
            <li>
              Completing an application for eligibility and enrollment in a Marketplace
              or SBE Qualified Health Plan or other government insurance affordability
              programs, such as Medicaid and CHIP or advance tax credits to help pay for
              Marketplace or SBE premiums
            </li>
            <li>Providing ongoing account maintenance and enrollment assistance, as necessary</li>
            <li>Responding to inquiries from the Marketplace or SBE regarding my application</li>
          </ul>

          <p>
            I understand that the Agent will not use or share my personally identifiable
            information (PII) for any purposes other than those listed above. The Agent
            will ensure that my PII is kept private and safe when collecting, storing,
            and using my PII for the stated purposes above.
          </p>

          <p>
            I understand that I do not have to share additional personal information
            about myself or my health with my Agent beyond what is required on the
            application for eligibility and enrollment purposes. I understand that my
            consent remains in effect until I revoke it, and I may revoke or modify my
            consent at any time by contacting my Agent.
          </p>

          <div className="aca-agent-details">
            <p><strong>Agent name:</strong> {agentName}</p>
            <p><strong>Agent NPN:</strong> {agentNpn}</p>
            <p><strong>Agent phone:</strong> {agentPhone}</p>
          </div>
        </div>

        <div className="aca-consent-form">
          <div className="aca-consent-field">
            <label>Date</label>
            <input value={new Date().toLocaleDateString()} disabled />
          </div>

          <div className="aca-consent-field">
            <label>Type Full Name Signature *</label>
            <input
              type="text"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              placeholder="Enter your full name"
            />
          </div>

          <label className="aca-consent-checkbox">
            <input
              type="checkbox"
              checked={agreement}
              onChange={() => setAgreement(!agreement)}
            />
            <span>
              I have read and agree to the ACA Consent terms above. I authorize my
              typed name to serve as my electronic signature.
            </span>
          </label>

          <button className="aca-consent-submit" onClick={handleSubmit}>
            Submit ACA Consent
          </button>
        </div>
      </div>
    </div>
  );
}

export default ACAConsentPage;